import React, {useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {NAVY, TEXTE, TEXTE_SECONDAIRE} from '../../tokens.js';
import {cascade, constatVariation, decomposition, finDeMois, moisDisponibles, nomMoisLong, tableauMois} from '../../selectors/variation.js';
import Cascade from '../Cascade.js';
import CircuitsSelect from '../CircuitsSelect.js';
import CarteAnalyse from './CarteAnalyse.js';
import PetitTableau from './PetitTableau.js';

// Lot 11 : « Pourquoi le tonnage varie ». Sélecteurs Circuit (Territoire ou un circuit) et Mois, phrase de constat calculée,
// cascade attendu -> causes -> résidu -> réel, tableau des 12 derniers mois. `lien(surcharge)` construit l'adresse de la carte.

const tonnes = (kg) => (kg === null || kg === undefined ? '—' : `${(kg / 1000).toFixed(1).replace('.', ',')} t`);
const tonnesSignees = (kg) => (kg === null || kg === undefined ? '—' : `${kg < 0 ? '−' : '+'}${(Math.abs(kg) / 1000).toFixed(1).replace('.', ',')} t`);

const puce = (actif) => ({height: 36, px: '14px', border: `1px solid ${actif ? NAVY : '#D5DAE1'}`, borderRadius: '999px', fontSize: 13, fontWeight: 600, cursor: 'pointer', font: 'inherit', backgroundColor: actif ? NAVY : '#FFFFFF', color: actif ? '#FFFFFF' : TEXTE});

export default function CarteVariation({index, signal, lien}) {
    const [circuit, setCircuit] = useState(null); // null = territoire extramuros
    const mois = useMemo(() => moisDisponibles(index), [index]);
    const [choisi, setChoisi] = useState(null);
    const moisCourant = choisi && mois.includes(choisi) ? choisi : mois[mois.length - 1];

    const extra = useMemo(() => [...index.patrimoineById.values()].filter((p) => p.groupe === 'extra').map((p) => p.id).sort((a, b) => a - b), [index]);
    const libelle = circuit === null ? 'le territoire' : `le circuit ${(index.patrimoineById.get(circuit) || {}).libelle || circuit}`;
    const d = useMemo(() => (moisCourant ? decomposition(index, circuit, moisCourant) : null), [index, circuit, moisCourant]);
    const etapes = useMemo(() => (moisCourant ? cascade(index, circuit, moisCourant) : null), [index, circuit, moisCourant]);
    const lignes = useMemo(() => tableauMois(index, circuit, mois).map((l) => ({
        cle: l.mois, mois: l.partiel ? `${nomMoisLong(l.mois)} (partiel)` : nomMoisLong(l.mois), attendu: tonnes(l.attendu), reel: tonnes(l.reel), explique: tonnesSignees(l.explique), residu: tonnesSignees(l.residu),
    })), [index, circuit, mois]);

    const lienCause = (etape) => {
        if (!['retiree', 'condamnee', 'defaut', 'nouvelle', 'residu'].includes(etape.cle)) return null;
        return lien({acte: 'collecte', style: 'causes', patrimoines: circuit === null ? extra : [circuit], du: `${moisCourant}-01`, au: finDeMois(moisCourant)});
    };

    return (
        <CarteAnalyse
            id="analyse-variation"
            titre="Pourquoi le tonnage varie"
            source="simule"
            constat={moisCourant ? constatVariation(d, libelle) : 'Aucune collecte à décomposer.'}
            signal={signal}
            lienCarte={moisCourant ? lien({acte: 'collecte', style: 'causes', patrimoines: circuit === null ? extra : [circuit], du: `${moisCourant}-01`, au: finDeMois(moisCourant)}) : null}
            pleineLargeur
        >
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', mb: '12px'}}>
                <Box component="button" type="button" aria-pressed={circuit === null} onClick={() => setCircuit(null)} sx={puce(circuit === null)}>Territoire</Box>
                <CircuitsSelect
                    index={index}
                    valeur={circuit === null ? [] : [circuit]}
                    onChange={(ids) => setCircuit(ids.find((id) => id !== circuit) ?? null)}
                    largeur={340}
                />
                <Box component="label" sx={{display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                    Mois
                    <Box component="select" value={moisCourant || ''} onChange={(e) => setChoisi(e.target.value)} sx={{height: 36, px: '12px', border: '1px solid #D5DAE1', borderRadius: '999px', backgroundColor: '#FFFFFF', fontSize: 13, fontWeight: 600, color: TEXTE, font: 'inherit'}}>
                        {mois.map((m) => <option key={m} value={m}>{nomMoisLong(m)}</option>)}
                    </Box>
                </Box>
            </Box>
            {etapes && (
                <>
                    <Cascade etapes={etapes} lien={lienCause} titre={`Cascade du tonnage, ${libelle}, ${nomMoisLong(moisCourant)}`}/>
                    <Box component="p" sx={{m: '4px 0 0', fontSize: 12, color: TEXTE_SECONDAIRE}}>
                        {`Attendu : ${d.source === 'N-1' ? 'même mois un an plus tôt' : 'moyenne des 3 mois précédents'}. Rouge : perte, vert : gain, gris : non expliqué. Cliquer une barre ouvre la carte des bornes (style Causes).`}
                    </Box>
                </>
            )}
            <Box sx={{mt: '16px'}}>
                <PetitTableau
                    colonnes={[{cle: 'mois', libelle: 'Mois'}, {cle: 'attendu', libelle: 'Attendu', droite: true}, {cle: 'reel', libelle: 'Réel', droite: true}, {cle: 'explique', libelle: 'Expliqué', droite: true}, {cle: 'residu', libelle: 'Résidu', droite: true}]}
                    lignes={lignes}
                    legende="Tonnage attendu, réel, expliqué et résidu sur les 12 derniers mois"
                />
            </Box>
        </CarteAnalyse>
    );
}
